// src/pages/Home/InfoClub.jsx

import React, { useEffect, useMemo, useState } from "react";
import createDOMPurify from "dompurify";
import { FaBullhorn, FaExpandAlt, FaTimes } from "react-icons/fa";

import Reveal from "../../components/Reveal/Reveal";
import { getSettings } from "../../services/settingsServices";

const INFOCLUB_DEFAULT_IMAGE = "/img/infoclub.png";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* ---------------------------
   DOMPurify instance + styles whitelist
---------------------------- */
const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : createDOMPurify;

const isColor = (value) => {
  const text = String(value || "").trim().toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED = {
    color: (value) => isColor(value),
    "background-color": (value) => isColor(value),
    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase()
      ),
    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase()
      ),
    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(String(value).trim().toLowerCase()),
    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase()
      ),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");
      if (separatorIndex === -1) return null;

      const property = rule.slice(0, separatorIndex).trim().toLowerCase();
      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED[property]?.(value) ? `${property}: ${value}` : null;
    })
    .filter(Boolean)
    .join("; ");
};

purify.removeAllHooks();

purify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName === "style") {
    const cleanStyle = sanitizeInlineStyle(data.attrValue);

    if (cleanStyle) {
      data.attrValue = cleanStyle;
    } else {
      data.keepAttr = false;
    }
  }
});

purify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const DEFAULT_SETTINGS = {
  infoclub_isactive: true,
  infoclub_title: "Info Club",
  infoclub_subtitle: "",
  infoclub_content:
    "<p>Ajoutez ici les informations importantes du club : nouveautés, consignes, inscriptions, événements ou messages à destination des familles.</p>",
  infoclub_image_url: "",
};

const InfoClubSkeleton = () => {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr]">
            <div className="space-y-4 p-6 sm:p-10">
              <div className="h-8 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
              <div className="h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
              <div className="mt-8 space-y-3">
                <div className="h-4 w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                <div className="h-4 w-[92%] animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                <div className="h-4 w-[84%] animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
              </div>
            </div>

            <div className="h-72 animate-pulse bg-slate-200 dark:bg-slate-800 lg:h-auto" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default function InfoClub() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [isImageOpen, setIsImageOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...(clubSettings || {}),
          infoclub_isactive:
            clubSettings?.infoclub_isactive === undefined ||
            clubSettings?.infoclub_isactive === null
              ? DEFAULT_SETTINGS.infoclub_isactive
              : !!clubSettings.infoclub_isactive,
          infoclub_image_url: clubSettings?.infoclub_image_url || "",
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isImageOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsImageOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isImageOpen]);

  const safeHtml = useMemo(() => {
    const raw = settings?.infoclub_content || DEFAULT_SETTINGS.infoclub_content;

    return purify.sanitize(raw, {
      USE_PROFILES: {
        html: true,
      },
      ADD_ATTR: ["style", "target", "rel"],
      FORBID_TAGS: ["style"],
    });
  }, [settings?.infoclub_content]);

  const infoClubImage = useMemo(() => {
    return (
      String(settings?.infoclub_image_url || "").trim() ||
      INFOCLUB_DEFAULT_IMAGE
    );
  }, [settings?.infoclub_image_url]);

  const handleImageError = (event) => {
    const img = event.currentTarget;

    if (img.dataset.fallbackApplied === "true") return;

    img.dataset.fallbackApplied = "true";
    img.src = INFOCLUB_DEFAULT_IMAGE;
  };

  if (loading) return <InfoClubSkeleton />;

  if (!settings?.infoclub_isactive) return null;

  const title = settings?.infoclub_title || DEFAULT_SETTINGS.infoclub_title;

  return (
    <>
      <section
        id="infoclub"
        className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-900 dark:bg-slate-950 dark:text-white lg:py-24"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_30%)]" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mt-14">
            <Reveal variant="zoom" delay={120}>
              <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80">
                <div className="absolute -left-16 -top-16 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
                <div className="absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />

                <div className="relative grid lg:grid-cols-[1.1fr_0.9fr]">
                  <div className="flex flex-col justify-center p-6 sm:p-10 lg:p-12">
                    <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-primary">
                      <FaBullhorn />
                      Informations du club
                    </div>

                    <div
                      className={cn(
                        "prose prose-slate max-w-none text-base leading-8 text-slate-600 dark:prose-invert dark:text-slate-300",
                        "prose-p:my-4 prose-p:text-[15px] sm:prose-p:text-[16px]",
                        "prose-headings:font-barlow prose-headings:font-bold prose-headings:text-slate-950 dark:prose-headings:!text-white",
                        "prose-strong:text-slate-950 dark:prose-strong:!text-white",
                        "prose-b:text-slate-950 dark:prose-b:!text-white",
                        "prose-a:font-bold prose-a:text-primary hover:prose-a:underline",
                        "prose-ul:my-5 prose-li:my-1",
                        "prose-blockquote:border-primary prose-blockquote:bg-orange-50 prose-blockquote:px-5 prose-blockquote:py-3 prose-blockquote:not-italic dark:prose-blockquote:bg-orange-500/10"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: safeHtml,
                      }}
                    />
                  </div>

                  <div className="relative min-h-[280px] overflow-hidden border-t border-slate-200 dark:border-white/10 lg:min-h-[520px] lg:border-l lg:border-t-0">
                    <button
                      type="button"
                      onClick={() => setIsImageOpen(true)}
                      className="group absolute inset-0 z-10 flex items-end justify-end text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40"
                      aria-label="Agrandir l’image Info Club"
                    >
                      <span className="m-4 inline-flex items-center gap-2 rounded-2xl bg-slate-950/70 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white opacity-90 shadow-lg backdrop-blur-md transition group-hover:bg-primary group-hover:opacity-100">
                        <FaExpandAlt />
                        Agrandir
                      </span>
                    </button>

                    <img
                      src={infoClubImage}
                      alt={title}
                      loading="lazy"
                      className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-105"
                      onError={handleImageError}
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent lg:bg-gradient-to-l lg:from-slate-950/30 lg:via-transparent lg:to-transparent" />

                    <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {isImageOpen ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label="Image Info Club agrandie"
          onClick={() => setIsImageOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsImageOpen(false)}
            className="absolute right-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
            aria-label="Fermer l’image agrandie"
          >
            <FaTimes />
          </button>

          <div
            className="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={infoClubImage}
              alt={title}
              className="max-h-[90vh] w-full object-contain"
              onError={handleImageError}
            />

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/80 to-transparent p-5">
              <p className="text-sm font-bold text-white">{title}</p>
              <p className="mt-1 text-xs text-white/70">
                Cliquez en dehors de l’image ou appuyez sur Échap pour fermer.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}